if (!customElements.get('product-family-picker')) {
  class ProductFamilyPicker extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('.product-family-picker__details');
      this.summaryImage = this.querySelector('[data-family-summary-image]');
      this.dataElement = this.querySelector('[data-family-products]');

      if (!this.details || !this.dataElement) return;

      try {
        this.familyData = JSON.parse(this.dataElement.textContent);
      } catch (error) {
        console.error('Product family picker data could not be parsed.', error);
        return;
      }

      this.itemsByHandle = new Map(
        this.familyData.items.map((item) => [item.handle, item])
      );

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleDetailsToggle = this.handleDetailsToggle.bind(this);

      document.addEventListener('on:variant:change', this.handleVariantChange);
      document.addEventListener('click', this.handleDocumentClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('click', this.handleOptionClick);
      this.details.addEventListener('toggle', this.handleDetailsToggle);

      this.sync(this.dataset.currentColor || '');
      this.initAnalytics();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.handleVariantChange);
      document.removeEventListener('click', this.handleDocumentClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleOptionClick);
      this.details?.removeEventListener('toggle', this.handleDetailsToggle);
      this.analyticsObserver?.disconnect();
    }

    handleVariantChange(event) {
      const productSection = this.closest('.js-product');

      if (!productSection || !productSection.contains(event.target)) return;

      const selectedColor = event.detail?.variant?.options?.[0] || '';
      this.dataset.currentColor = selectedColor;
      this.sync(selectedColor);
    }

    handleDocumentClick(event) {
      if (this.details.open && !this.contains(event.target)) {
        this.details.removeAttribute('open');
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.details.open) return;

      this.details.removeAttribute('open');
      this.details.querySelector('summary')?.focus();
    }

    handleOptionClick(event) {
      if (this.dataset.familyAnalytics !== 'true') return;

      const card = event.target.closest('[data-family-card]');
      if (!card || !this.contains(card)) return;

      this.publishAnalytics('select', card);
    }

    handleDetailsToggle() {
      if (
        this.dataset.familyAnalytics !== 'true' ||
        !this.details.open ||
        this.openTracked
      ) {
        return;
      }

      this.openTracked = true;
      this.publishAnalytics('open');
    }

    initAnalytics() {
      if (this.dataset.familyAnalytics !== 'true') return;

      const trackView = () => {
        if (this.viewTracked) return;
        this.viewTracked = true;
        this.dataset.familyViewTracked = 'true';
        this.publishAnalytics('view');
      };

      if ('IntersectionObserver' in window) {
        this.analyticsObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            trackView();
            this.analyticsObserver.disconnect();
          }
        }, { threshold: 0.25 });
        this.analyticsObserver.observe(this);
      } else {
        trackView();
      }
    }

    analyticsPayload(card = null) {
      const targetPrice = Number(card?.dataset.targetPrice || this.dataset.currentPrice || 0);
      const sourcePrice = Number(this.dataset.currentPrice || 0);

      return {
        family_id: this.dataset.familyId || '',
        source_product_id: this.dataset.productId || '',
        source_handle: this.dataset.currentHandle || '',
        target_product_id: card?.dataset.targetProductId || this.dataset.productId || '',
        target_handle: card?.dataset.familyHandle || this.dataset.currentHandle || '',
        source_price: sourcePrice,
        target_price: targetPrice,
        price_delta: targetPrice - sourcePrice,
        selected_color: this.dataset.currentColor || '',
        color_preserved: card ? card.dataset.hasColorMatch === 'true' : true,
        is_current: card ? card.classList.contains('is-current') : true,
        option_position: Number(card?.dataset.optionPosition || 0),
      };
    }

    publishAnalytics(action, card = null) {
      const eventName = `adore_family_picker_${action}`;
      const payload = this.analyticsPayload(card);

      window.dataLayer = window.dataLayer || [];
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: eventName,
          family_picker: payload,
        });
      }

      document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));

      if (window.Shopify?.analytics?.publish) {
        try {
          const publication = window.Shopify.analytics.publish(eventName, payload);
          if (publication?.catch) publication.catch(() => {});
        } catch (error) {
          // The DOM event remains available for GTM preview and local QA.
        }
      }
    }

    getSelection(item, selectedColor) {
      const exactColorVariant = selectedColor
        ? item.variants.find((variant) => variant.color === selectedColor)
        : null;

      return {
        hasExactColor: Boolean(exactColorVariant),
        variant:
          exactColorVariant ||
        item.variants.find((variant) => variant.id === item.defaultVariantId) ||
          item.variants[0],
      };
    }

    sync(selectedColor) {
      this.querySelectorAll('[data-family-card]').forEach((card) => {
        const item = this.itemsByHandle.get(card.dataset.familyHandle);
        if (!item) return;

        const selection = this.getSelection(item, selectedColor);
        const targetVariant = selection.variant;
        if (!targetVariant) return;

        card.href = `${item.url}?variant=${encodeURIComponent(targetVariant.id)}`;
        card.dataset.hasColorMatch = selection.hasExactColor ? 'true' : 'false';

        const cardImage = card.querySelector('[data-family-card-image]');
        const imageUrl = targetVariant.image || item.defaultImage;
        if (cardImage && imageUrl) cardImage.src = imageUrl;

        const fallbackBadge = card.querySelector('[data-family-fallback-badge]');
        const fallbackMessage = card.querySelector(
          '[data-family-fallback-message]'
        );
        const showFallback = Boolean(selectedColor) && !selection.hasExactColor;

        if (fallbackBadge) {
          fallbackBadge.hidden = !showFallback;
          fallbackBadge.textContent = showFallback
            ? `Doar ${targetVariant.color}`
            : '';
        }

        if (fallbackMessage) {
          fallbackMessage.textContent = showFallback
            ? `Acest model nu este disponibil în ${selectedColor}. Se va deschide varianta ${targetVariant.color}.`
            : '';
        }

        if (
          item.handle === this.dataset.currentHandle &&
          this.summaryImage &&
          imageUrl
        ) {
          this.summaryImage.src = imageUrl;
        }
      });
    }
  }

  customElements.define('product-family-picker', ProductFamilyPicker);
}
